import { motion } from "framer-motion";
import { ArrowRight, FileText, MousePointer2, Move3d, ZoomIn } from "lucide-react";
import { Link } from "react-router-dom";
import { useContent } from "@/content/ContentContext";
import { Logo } from "./Logo";
import { LangToggle } from "./LangToggle";
import { useT } from "@/content/i18n";
import { CvDownload } from "./Extras";

const ease = [0.22, 1, 0.36, 1];

const Line = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

const Marquee = () => {
  const t = useT();
  return (
  <div className="marquee absolute bottom-[9vh] left-0 right-0 overflow-hidden border-y border-[#F7F4EE]/10 py-3">
    <div className="marquee-track">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center gap-10 pr-10" aria-hidden={k === 1}>
          {t("words").map((w) => (
            <span key={w} className="flex items-center gap-10 font-display text-lg tracking-[0.2em] text-[#F7F4EE]/55 sm:text-xl">
              {w}<span className="h-1.5 w-1.5 rounded-full bg-[#E69C24]" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
  );
};

const hints = [
  { icon: Move3d, key: "hintRotate" },
  { icon: ZoomIn, key: "hintZoom" },
  { icon: MousePointer2, key: "hintClick" },
];

export const Intro = ({ onEnter }) => {
  const { profile } = useContent();
  const t = useT();
  return (
  <motion.div
    data-testid="intro-overlay-container"
    className="fixed inset-0 z-[60] overflow-hidden bg-[#12100E]/80 backdrop-blur-[6px]"
    initial={{ opacity: 1 }}
    exit={{ opacity: 0, transition: { duration: 1.2, ease } }}
  >
    <motion.div className="absolute left-0 right-0 top-0 z-10 h-[9vh] bg-black" exit={{ y: "-100%" }} transition={{ duration: 1, ease }} />
    <motion.div className="absolute bottom-0 left-0 right-0 z-10 h-[9vh] bg-black" exit={{ y: "100%" }} transition={{ duration: 1, ease }} />
    <div className="no-scrollbar relative mx-auto flex h-full max-w-6xl flex-col overflow-y-auto px-6 pb-[18vh] pt-[11vh] sm:px-10 sm:pt-[12vh]">
      <div className="my-auto pt-4">
      <motion.div
        className="mb-5 flex items-center gap-3 sm:mb-8"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2, ease }}
      >
        <Logo className="h-9 w-9 shrink-0" />
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E69C24]">{t("tagline")}</span>
        <span className="ml-auto shrink-0"><LangToggle /></span>
      </motion.div>
      <h1 className="font-display text-[13vw] font-semibold leading-[0.95] text-[#F7F4EE] sm:text-6xl lg:text-8xl">
        <Line delay={0.35}>{t("enter1")}</Line>
        <Line delay={0.5}>{t("enter2")} <em className="not-italic text-[#E69C24]">{t("enter3")}</em></Line>
      </h1>
      <motion.p
        className="mt-6 max-w-xl text-sm leading-relaxed text-[#C5BDB3] sm:text-base"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.9, ease }}
      >
        {t("iAm")} <span className="text-[#F7F4EE]">{profile.firstName} {profile.lastName}</span>{profile.role ? `, ${profile.role.toLowerCase()}` : ""}.
        {" "}{t("introText")}
      </motion.p>
      <motion.div
        className="mt-6 flex flex-col gap-6 sm:mt-8"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.1, ease }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <button data-testid="intro-start-button" onClick={onEnter} className="btn-gold group w-fit">
            {t("enterRoom")}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <Link to="/cv" data-testid="intro-classic-button" className="cv-cta">
            <FileText className="h-4 w-4 text-[#E69C24]" />{t("cvNoRoom")}
          </Link>
          <CvDownload className="chip !px-5 !py-3 !text-sm sm:!py-3.5" testId="intro-cv-download" compact={false} />
        </div>
        <ul className="hidden flex-wrap gap-x-5 gap-y-2 sm:flex">
          {hints.map(({ icon: Icon, key }) => (
            <li key={key} className="flex items-center gap-2 text-xs text-[#C5BDB3]">
              <Icon className="h-3.5 w-3.5 text-[#E69C24]" />{t(key)}
            </li>
          ))}
        </ul>
      </motion.div>
      </div>
    </div>
    <Marquee />
  </motion.div>
  );
};
